import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  StateCircleIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { REVIEW_STATES } from '@/constants';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

export function MentionFilterSegmentReviewState({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  reviewStateOptions = REVIEW_STATES,
}: TFilterProps) {
  const { t } = useTranslation();
  const { review_states: state, setFilters: setState } = useProgramBroadcastFilters();
  const reviewStates = useMemo(() => {
    const allOption = {
      value: 'all',
      label: t('Seleccionar todos') as string,
      isSelectAll: true,
      childrenIds: reviewStateOptions.map(s => String(s.value)),
      children: [],
    };
    return [
      allOption,
      ...reviewStateOptions.map(state => ({
        value: String(state.value),
        label: t(state.label) as string,
      })),
    ];
  }, []);

  const allSelected = state.length === reviewStateOptions.length;
  const noneSelected = state.length === 0;
  const someSelected = !allSelected && !noneSelected;

  const handleChangeState = (value: unknown) => {
    if (Array.isArray(value) && value.includes('all')) {
      if (allSelected || someSelected) {
        setState({ review_states: [] });
      } else {
        setState({ review_states: reviewStateOptions.map(s => s.value) });
      }
    } else if (Array.isArray(value)) {
      const last = value[value.length - 1];
      const lastNum = Number(last);
      if (state.includes(lastNum)) {
        const filtered = state.filter(v => v !== lastNum);
        setState({ review_states: filtered });
      } else {
        const filtered = value.filter(v => v !== 'all');
        const asNumbers = filtered.map(Number);
        const unique = Array.from(new Set(asNumbers));
        setState({ review_states: unique });
      }
    } else {
      setState({ review_states: [] });
    }
  };

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Estado de revisión')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && reviewStates.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <StateCircleIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Estado de revisión')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          <Listbox
            dark={isDark}
            mode="multiple"
            items={reviewStates}
            onChange={handleChangeState}
            identifiers={{
              key: 'value',
              value: 'value',
              label: 'label',
              children: 'options',
            }}
            selected={state.map(String) as string[]}
            onClose={onClose}
            noSort
          />
        </div>
      )}
    </div>
  );
}
